import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'

import {
  resetMemberPassword,
  type AdminMemberAccount,
  type AdminMemberPasswordResetResult,
} from './api'

interface AdminMemberResetPasswordModalProps {
  member: AdminMemberAccount
  onClose: () => void
  onReset: (result: AdminMemberPasswordResetResult & { email: string }) => void
  open: boolean
}

export function AdminMemberResetPasswordModal({
  member,
  onClose,
  onReset,
  open,
}: AdminMemberResetPasswordModalProps) {
  const { t } = useTranslation('adminOrganization')
  const { showFeedback } = useFeedback()

  const resetPasswordMutation = useMutation({
    mutationFn: () => resetMemberPassword({ userId: member.id }),
    onError: () => {
      showFeedback({
        message: t('errors.submitFailed'),
        tone: 'error',
      })
    },
    onSuccess: (result) => {
      showFeedback({
        message: t('success.resetPassword'),
        tone: 'success',
      })
      onReset({ ...result, email: member.email })
      onClose()
    },
  })

  return (
    <Modal
      closeLabel={t('memberAccounts.resetPasswordModal.close')}
      footer={
        <>
          <Button
            disabled={resetPasswordMutation.isPending}
            onClick={onClose}
            size="sm"
            variant="secondary"
          >
            {t('actions.cancel')}
          </Button>
          <Button
            form="admin-member-reset-password-form"
            isLoading={resetPasswordMutation.isPending}
            size="sm"
            type="submit"
          >
            {t('memberAccounts.resetPasswordModal.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={t('memberAccounts.resetPasswordModal.title')}
    >
      <form
        className="grid gap-3"
        id="admin-member-reset-password-form"
        onSubmit={(event) => {
          event.preventDefault()
          resetPasswordMutation.mutate()
        }}
      >
        <p className="text-sm leading-6 text-[var(--color-text)]">
          {t('memberAccounts.resetPasswordModal.description', { email: member.email })}
        </p>
        <p className="text-sm leading-6 text-[var(--color-text-muted)]">
          {t('memberAccounts.resetPasswordModal.impact')}
        </p>
      </form>
    </Modal>
  )
}
